import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectConversations,
  selectMessages,
  selectCurrentConversation,
  selectChatLoading,
  selectTypingUsers,
  selectSearchQuery,
  selectSearchResults,
  setCurrentConversation,
  setSearchQuery,
  clearError,
} from '../../store/chatSlice';
import {
  fetchConversations,
  subscribeToConversations,
  fetchMessages,
  subscribeToMessages,
  sendMessage,
  markConversationAsRead,
  searchConversations,
  initializeChat,
  subscribeToUserStatus,
  blockUser,
  unblockUser,
  reportUser,
  deleteConversation,
  sendImage,
  sendFile,
} from '../../store/chatThunks';
import {
  MessageCircle,
  Send,
  Search,
  MoreVertical,
  Image as ImageIcon,
  Paperclip,
  X,
  UserMinus,
  Flag,
  Trash2,
  Reply,
} from 'lucide-react';

const formatTime = timestamp => {
  if (!timestamp || timestamp === 0) {
    return '';
  }

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
      });
    }
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

const formatLastSeen = (ms, t) => {
  if (!ms) return t('chat.offline');
  try {
    const diff = Date.now() - ms;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return t('chat.seenRecently');
    if (minutes < 60) return t('chat.minutesAgo', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('chat.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return t('chat.daysAgo', { count: days });
  } catch (error) {
    console.error('Error formatting last seen:', error);
    return t('chat.offline');
  }
};

export default function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const conversations = useAppSelector(selectConversations);
  const messages = useAppSelector(selectMessages);
  const currentConversation = useAppSelector(selectCurrentConversation);
  const isLoading = useAppSelector(selectChatLoading);
  const typingUsers = useAppSelector(selectTypingUsers);
  const searchQuery = useAppSelector(selectSearchQuery);
  const searchResults = useAppSelector(selectSearchResults);

  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [peerStatus, setPeerStatus] = useState({
    isOnline: false,
    lastSeen: 0,
  });
  const [isBlocked, setIsBlocked] = useState(false);
  const [isUserBlockedByMe, setIsUserBlockedByMe] = useState(false);
  const [showChatOptions, setShowChatOptions] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedMessageForReply, setSelectedMessageForReply] = useState(null);
  const [imageViewerUrl, setImageViewerUrl] = useState(null);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [sendingAttachment, setSendingAttachment] = useState(false);
  const [showAttachOptions, setShowAttachOptions] = useState(false);
  const [messagesUnsub, setMessagesUnsub] = useState(null);
  const [userStatusUnsub, setUserStatusUnsub] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Initialize chat on component mount
  useEffect(() => {
    if (user) {
      const cleanup = dispatch(initializeChat());
      return () => {
        if (cleanup && typeof cleanup === 'function') {
          cleanup();
        }
        if (messagesUnsub) {
          messagesUnsub();
        }
        if (userStatusUnsub) {
          userStatusUnsub();
        }
      };
    }
  }, [user, dispatch]);

  // Handle opening conversation from URL
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conversation = conversations.find(
        conv => conv.id === conversationId,
      );
      if (conversation && conversation.id !== currentConversation?.id) {
        handleConversationSelect(conversation);
      }
    }
  }, [conversationId, conversations]);

  // Handle opening conversation when currentConversation is set
  useEffect(() => {
    if (currentConversation) {
      setIsBlocked(false);
      setIsUserBlockedByMe(false);
      checkBlockStatus();
    }
  }, [currentConversation]);

  // Check block status
  const checkBlockStatus = useCallback(async () => {
    if (!currentConversation?.participantId || !user?.uid) return;
    // This would need to be implemented in firebaseChatService
  }, [currentConversation?.participantId, user?.uid]);

  const handleConversationSelect = useCallback(
    async conversation => {
      // Cleanup previous subscriptions
      if (messagesUnsub) {
        messagesUnsub();
        setMessagesUnsub(null);
      }
      if (userStatusUnsub) {
        userStatusUnsub();
        setUserStatusUnsub(null);
      }

      dispatch(setCurrentConversation(conversation));

      if (conversation.isTemporary) {
        console.log('Opening temporary conversation:', conversation);
      } else {
        // Fetch initial messages
        await dispatch(fetchMessages(conversation.id));

        // Subscribe to real-time messages
        const unsub = dispatch(subscribeToMessages(conversation.id));
        if (unsub && typeof unsub === 'function') {
          setMessagesUnsub(() => unsub);
        }
      }

      if (conversation.participantId) {
        const statusUnsub = dispatch(
          subscribeToUserStatus(conversation.participantId, status => {
            setPeerStatus(status);
          }),
        );
        if (statusUnsub && typeof statusUnsub === 'function') {
          setUserStatusUnsub(() => statusUnsub);
        }
      }

      dispatch(markConversationAsRead(conversation.id));
      navigate(`/chat/${conversation.id}`);
    },
    [dispatch, navigate, messagesUnsub, userStatusUnsub],
  );

  const handleSendMessage = async () => {
    if (messageText.trim() === '' || !currentConversation) return;
    const messageToSend = messageText.trim();
    const replyText = replyToMessage ? replyToMessage.text : undefined;
    const replyType = replyToMessage ? replyToMessage.type : undefined;
    const replyImageUrl = replyToMessage ? replyToMessage.imageUrl : undefined;
    const replyFileName = replyToMessage ? replyToMessage.fileName : undefined;
    setMessageText('');
    setIsTyping(false);
    try {
      setReplyToMessage(null);
      await dispatch(
        sendMessage(
          currentConversation.id,
          messageToSend,
          replyText,
          replyType,
          replyImageUrl,
          replyFileName,
        ),
      );
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      setMessageText(messageToSend);
      console.error('Error sending message:', error);
    }
  };

  const handleSearch = q => {
    const query = typeof q === 'string' ? q : String(q ?? '');
    dispatch(setSearchQuery(query));
    if (query.trim().length > 0) {
      setIsSearching(true);
      dispatch(searchConversations(query));
    } else {
      setIsSearching(false);
    }
  };

  const handleDeleteConversation = async conversationId => {
    if (confirm(t('chat.confirmDeleteConversation'))) {
      try {
        await dispatch(deleteConversation(conversationId));
        if (currentConversation?.id === conversationId) {
          dispatch(setCurrentConversation(null));
          navigate('/chat');
        }
      } catch (error) {
        alert(t('chat.failedDeleteConversation'));
      }
    }
  };

  const handleBlockUser = () => {
    if (
      confirm(
        t('chat.confirmBlockUser', {
          name: currentConversation?.participantName || t('chat.thisUser'),
        }),
      )
    ) {
      dispatch(blockUser(currentConversation?.participantId));
      setIsUserBlockedByMe(true);
      setShowChatOptions(false);
    }
  };

  const handleUnblockUser = () => {
    if (
      confirm(
        t('chat.confirmUnblockUser', {
          name: currentConversation?.participantName || t('chat.thisUser'),
        }),
      )
    ) {
      dispatch(unblockUser(currentConversation?.participantId));
      setIsUserBlockedByMe(false);
      setShowChatOptions(false);
    }
  };

  const handleReportUser = () => {
    if (
      confirm(
        t('chat.confirmReportUser', {
          name: currentConversation?.participantName || t('chat.thisUser'),
        }),
      )
    ) {
      dispatch(
        reportUser({
          reportedUserId: currentConversation?.participantId,
          reason: 'Inappropriate behavior',
        }),
      );
      setShowChatOptions(false);
      alert(t('chat.userReported'));
    }
  };

  const handleImageSelect = e => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setPendingAttachment({
        type: 'image',
        file,
        fileName: file.name,
        uri: URL.createObjectURL(file),
      });
      setShowAttachOptions(false);
    }
  };

  const handleFileSelect = e => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingAttachment({
        type: 'file',
        file,
        fileName: file.name,
        uri: URL.createObjectURL(file),
      });
      setShowAttachOptions(false);
    }
  };

  const handleSendAttachment = async () => {
    if (!currentConversation || !pendingAttachment || sendingAttachment) return;
    try {
      setSendingAttachment(true);
      if (pendingAttachment.type === 'image') {
        await dispatch(
          sendImage(currentConversation.id, {
            file: pendingAttachment.file,
            fileName: pendingAttachment.fileName,
            type: pendingAttachment.file.type,
          }),
        );
      } else if (pendingAttachment.type === 'file') {
        await dispatch(
          sendFile(currentConversation.id, {
            file: pendingAttachment.file,
            fileName: pendingAttachment.fileName,
            type: pendingAttachment.file.type,
            size: pendingAttachment.file.size,
          }),
        );
      }
      setPendingAttachment(null);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error sending attachment:', error);
    } finally {
      setSendingAttachment(false);
    }
  };

  const renderMessage = message => {
    const isCurrentUser = message.senderId === user?.uid;
    const isImageMessage = message.type === 'image' && !!message.imageUrl;

    return (
      <div
        key={message.id}
        className={`flex mb-4 group ${
          isCurrentUser ? 'justify-end' : 'justify-start'
        }`}
      >
        <div
          className={`max-w-[70%] rounded-2xl px-4 py-2 relative ${
            isCurrentUser
              ? 'bg-black dark:bg-slate-700 text-yellow-400'
              : 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white'
          }`}
        >
          {/* Reply Button - appears on hover */}
          <button
            onClick={() => {
              setSelectedMessageForReply(message);
              setShowReplyModal(true);
            }}
            className="absolute -top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white dark:bg-slate-700 rounded-full shadow-lg border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600"
            title={t('chat.replyToMessage')}
          >
            <Reply className="h-3.5 w-3.5 text-gray-600 dark:text-slate-300" />
          </button>

          {message.replyToText && (
            <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 rounded text-xs">
              <div className="text-yellow-600 dark:text-yellow-400 font-bold mb-1">
                {t('chat.repliedTo')}
              </div>
              <div className="text-gray-700 dark:text-slate-300">
                {message.replyToText}
              </div>
            </div>
          )}
          {isImageMessage ? (
            <img
              src={message.imageUrl}
              alt="Sent image"
              className="max-w-full rounded-lg cursor-pointer"
              onClick={() => setImageViewerUrl(message.imageUrl)}
            />
          ) : message.type === 'file' ? (
            <a
              href={message.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Paperclip className="h-4 w-4" />
              <span>{message.fileName || 'File'}</span>
            </a>
          ) : (
            <p className="text-sm">{message.text}</p>
          )}
          <p
            className={`text-xs mt-1 ${
              isCurrentUser
                ? 'text-yellow-300 dark:text-yellow-400'
                : 'text-gray-500 dark:text-slate-400'
            }`}
          >
            {formatTime(message.createdAt)}
          </p>
        </div>
      </div>
    );
  };

  const renderConversation = conversation => {
    const unreadCount = conversation.unreadCount?.[user?.uid] || 0;
    const lastMessageTime = conversation.lastMessageTime || 0;
    const isSelected = currentConversation?.id === conversation.id;

    return (
      <div
        key={conversation.id}
        className={`flex items-center gap-3 p-4 border-b border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer ${
          isSelected ? 'bg-gray-100 dark:bg-slate-800' : ''
        }`}
        onClick={() => handleConversationSelect(conversation)}
      >
        <div className="w-12 h-12 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center text-yellow-400 font-bold text-lg flex-shrink-0">
          {conversation.participantName?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-gray-900 dark:text-white truncate">
              {conversation.participantName || t('common.unknownUser')}
            </h3>
            {lastMessageTime > 0 && (
              <span className="text-xs text-gray-500 dark:text-slate-400 flex-shrink-0 ml-2">
                {formatTime(lastMessageTime)}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-400 truncate">
            {conversation.lastMessage || t('chat.noMessagesYet')}
          </p>
        </div>
        {unreadCount > 0 && (
          <div className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
            {unreadCount}
          </div>
        )}
      </div>
    );
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (currentConversation && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, currentConversation]);

  return (
    <div className="h-screen bg-gray-50 dark:bg-slate-950 flex flex-col overflow-hidden">
      <MainNavbar />
      <div className="flex flex-1 pt-24 overflow-hidden">
        {/* Left Panel - Conversations List */}
        <div className="w-1/3 border-r border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-800 flex-shrink-0">
            <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-4">
              {t('chat.title')}
            </h1>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder={t('chat.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-yellow-500"></div>
              </div>
            ) : (
              <div>
                {(isSearching ? searchResults : conversations).map(conv =>
                  renderConversation(conv),
                )}
                {conversations.length === 0 && !isLoading && (
                  <div className="p-8 text-center text-gray-500 dark:text-slate-400">
                    <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-slate-700" />
                    <p>{t('chat.noConversations')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Chat */}
        <div className="flex-1 flex flex-col bg-white dark:bg-slate-900 overflow-hidden">
          {currentConversation ? (
            <>
              {/* Chat Header */}
              <div className="border-b border-gray-200 dark:border-slate-800 p-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center text-yellow-400 font-bold">
                    {currentConversation?.participantName
                      ?.charAt(0)
                      ?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h2 className="font-bold text-gray-900 dark:text-white">
                      {currentConversation?.participantName ||
                        currentConversation?.conversationName ||
                        t('common.unknownUser')}
                    </h2>
                    <p
                      className={`text-xs ${
                        peerStatus.isOnline
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-slate-400'
                      }`}
                    >
                      {peerStatus.isOnline
                        ? t('chat.online')
                        : formatLastSeen(peerStatus.lastSeen, t)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowChatOptions(!showChatOptions)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg relative"
                >
                  <MoreVertical className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                  {showChatOptions && (
                    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 min-w-[200px] z-10">
                      {isUserBlockedByMe ? (
                        <button
                          onClick={handleUnblockUser}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-green-600 dark:text-green-400"
                        >
                          <UserMinus className="h-4 w-4" />
                          Unblock User
                        </button>
                      ) : (
                        <button
                          onClick={handleBlockUser}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-red-600 dark:text-red-400"
                        >
                          <UserMinus className="h-4 w-4" />
                          Block User
                        </button>
                      )}
                      <button
                        onClick={handleReportUser}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-yellow-600 dark:text-yellow-400"
                      >
                        <Flag className="h-4 w-4" />
                        Report User
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteConversation(currentConversation?.id)
                        }
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-red-600 dark:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete Chat
                      </button>
                    </div>
                  )}
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 dark:text-slate-400">
                      {t('chat.noMessagesStart')}
                    </p>
                  </div>
                ) : (
                  <>
                    {messages.map(message => renderMessage(message))}
                    {typingUsers.length > 0 && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 dark:bg-slate-800 rounded-2xl px-4 py-2">
                          <p className="text-sm text-gray-500 dark:text-slate-400 italic">
                            {t('chat.typing')}
                          </p>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Attachment Preview */}
              {pendingAttachment && (
                <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-t border-gray-200 dark:border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {pendingAttachment.type === 'image' ? (
                      <img
                        src={pendingAttachment.uri}
                        alt="Preview"
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <Paperclip className="h-8 w-8 text-gray-600 dark:text-slate-400" />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {pendingAttachment.type === 'image'
                          ? t('chat.photo')
                          : t('chat.file')}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-slate-400">
                        {pendingAttachment.fileName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPendingAttachment(null)}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg"
                    >
                      <X className="h-4 w-4 text-gray-600 dark:text-slate-400" />
                    </button>
                    <button
                      onClick={handleSendAttachment}
                      disabled={sendingAttachment}
                      className="px-4 py-2 bg-black dark:bg-slate-700 text-yellow-400 rounded-lg font-bold hover:bg-gray-900 dark:hover:bg-slate-800 disabled:opacity-50"
                      >
                      {sendingAttachment
                        ? t('chat.sending')
                        : t('chat.send')}
                    </button>
                  </div>
                </div>
              )}

              {/* Reply Preview */}
              {replyToMessage && (
                <div className="px-4 py-2 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-800 flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-yellow-600 dark:text-yellow-400 mb-1">
                      {t('chat.replyingTo')}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-slate-300">
                      {replyToMessage.text}
                    </p>
                  </div>
                  <button
                    onClick={() => setReplyToMessage(null)}
                    className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg"
                  >
                    <X className="h-4 w-4 text-gray-600 dark:text-slate-400" />
                  </button>
                </div>
              )}

              {/* Input */}
              <div className="border-t border-gray-200 dark:border-slate-800 p-4 flex-shrink-0">
                <div className="flex items-end gap-2">
                  <div className="relative">
                    <button
                      onClick={() => setShowAttachOptions(!showAttachOptions)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg"
                    >
                      <Paperclip className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                    </button>
                    {showAttachOptions && (
                      <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 p-2">
                        <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded cursor-pointer">
                          <ImageIcon className="h-4 w-4 text-gray-600 dark:text-slate-400" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            Photo
                          </span>
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            className="hidden"
                          />
                        </label>
                        <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded cursor-pointer">
                          <Paperclip className="h-4 w-4 text-gray-600 dark:text-slate-400" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            File
                          </span>
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  <textarea
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={t('chat.inputPlaceholder')}
                    className="flex-1 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 resize-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    rows={1}
                    disabled={isBlocked || isUserBlockedByMe}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={
                      !messageText.trim() || isBlocked || isUserBlockedByMe
                    }
                    className="p-2 bg-black dark:bg-slate-700 text-yellow-400 rounded-xl hover:bg-gray-900 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </div>
                {(isBlocked || isUserBlockedByMe) && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    {isBlocked
                      ? t('chat.blockedByUser')
                      : t('chat.blockedUser')}
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-slate-700" />
                <p className="text-gray-500 dark:text-slate-400 text-lg font-semibold">
                  {t('chat.selectConversation')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {imageViewerUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={() => setImageViewerUrl(null)}
        >
          <img
            src={imageViewerUrl}
            alt="Full size"
            className="max-w-full max-h-full"
          />
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedMessageForReply && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => {
            setShowReplyModal(false);
            setSelectedMessageForReply(null);
          }}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('chat.replyToMessageModalTitle')}
              </h3>
              <button
                onClick={() => {
                  setShowReplyModal(false);
                  setSelectedMessageForReply(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-slate-400" />
              </button>
            </div>

            {/* Original Message Preview */}
            <div className="mb-4 p-4 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-2">
                {t('chat.originalMessage')}
              </p>
              {selectedMessageForReply.type === 'image' &&
              selectedMessageForReply.imageUrl ? (
                <img
                  src={selectedMessageForReply.imageUrl}
                  alt="Message image"
                  className="max-w-full rounded-lg"
                />
              ) : selectedMessageForReply.type === 'file' ? (
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <Paperclip className="h-4 w-4" />
                  <span className="text-sm">
                    {selectedMessageForReply.fileName || 'File'}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-700 dark:text-slate-300">
                  {selectedMessageForReply.text}
                </p>
              )}
            </div>

            {/* Reply Input */}
            <textarea
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
                placeholder={t('chat.replyPlaceholder')}
              className="w-full border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 resize-none placeholder:text-gray-400 dark:placeholder:text-slate-500 mb-4"
              rows={4}
            />

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowReplyModal(false);
                  setSelectedMessageForReply(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition"
              >
                {t('chat.cancel')}
              </button>
              <button
                onClick={() => {
                  if (messageText.trim() && selectedMessageForReply) {
                    setReplyToMessage({
                      text: selectedMessageForReply.text,
                      type: selectedMessageForReply.type,
                      imageUrl: selectedMessageForReply.imageUrl,
                      fileName: selectedMessageForReply.fileName,
                    });
                    setShowReplyModal(false);
                    setSelectedMessageForReply(null);
                  }
                }}
                disabled={!messageText.trim()}
                className="flex-1 px-4 py-2 bg-black dark:bg-slate-700 text-yellow-400 rounded-xl font-semibold hover:bg-gray-900 dark:hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Reply className="h-4 w-4" />
                {t('chat.reply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
